/**
 * Custom hook for managing irrigation plan history
 * Handles history filters, pagination, and data fetching
 */

import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import {
  fetchIrrigationPlanHistoryAtom,
  irrigationPlanHistoryFiltersAtom,
  irrigationPlanHistoryHasMoreAtom,
  irrigationPlanHistoryLoadingAtom,
  irrigationPlanHistoryPaginationAtom,
  irrigationPlanHistoryRequestKeyAtom,
  irrigationPlanHistorySelectionReadyAtom,
} from "@/store/data";

interface UseIrrigationPlanHistoryProps {
  /** The current plan data (null in create mode) */
  plan: { id: string | number } | null;
  /** ID of the project this plan belongs to */
  projectId: string | null;
  /** Currently active tab */
  activeTab: string;
}

interface UseIrrigationPlanHistoryReturn {
  historyHasMore: boolean;
  historyLoading: boolean;
  handleLoadMore: () => void;
  handleRetry: () => void;
}

/**
 * Hook for managing irrigation plan history
 */
export function useIrrigationPlanHistory({
  plan,
  projectId,
  activeTab,
}: UseIrrigationPlanHistoryProps): UseIrrigationPlanHistoryReturn {
  const setHistoryFilters = useSetAtom(irrigationPlanHistoryFiltersAtom);
  const setHistoryPagination = useSetAtom(irrigationPlanHistoryPaginationAtom);
  const fetchHistory = useSetAtom(fetchIrrigationPlanHistoryAtom);
  const historySelectionReady = useAtomValue(irrigationPlanHistorySelectionReadyAtom);
  const historyRequestKey = useAtomValue(irrigationPlanHistoryRequestKeyAtom);
  const historyHasMore = useAtomValue(irrigationPlanHistoryHasMoreAtom);
  const historyLoading = useAtomValue(irrigationPlanHistoryLoadingAtom);

  // Update history filters when switching to history tab
  useCallback(() => {
    if (activeTab !== "history") return;
    if (!plan) return;
    if (!projectId) return;

    const planIdValue = plan.id ? String(plan.id) : null;
    const projectIdValue = String(projectId);
    if (!planIdValue) return;

    setHistoryFilters((prev) => {
      if (prev.scope === "plan" && prev.planId === planIdValue && prev.projectId === projectIdValue) {
        return prev;
      }
      return {
        ...prev,
        scope: "plan",
        projectId: projectIdValue,
        planId: planIdValue,
      };
    });
  }, [activeTab, plan, projectId, setHistoryFilters]);

  // Fetch history when filters are ready
  useCallback(() => {
    if (activeTab !== "history") return;
    if (!historySelectionReady) return;
    fetchHistory();
  }, [activeTab, fetchHistory, historyRequestKey, historySelectionReady]);

  /**
   * Load more history entries
   */
  const handleLoadMore = useCallback(() => {
    if (historyLoading) return;
    setHistoryPagination((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  }, [historyLoading, setHistoryPagination]);

  /**
   * Retry fetching history
   */
  const handleRetry = useCallback(() => {
    if (!historySelectionReady) return;
    fetchHistory();
  }, [fetchHistory, historySelectionReady]);

  return {
    historyHasMore,
    historyLoading,
    handleLoadMore,
    handleRetry,
  };
}
